import { lazy, Suspense, useEffect, useState } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Layout } from "./components/Layout";
import { Loading } from "./components/UI";
import { api, type SessionUser, setCsrfToken } from "./lib/api";
import { LoginPage } from "./pages/LoginPage";

const OverviewPage = lazy(() => import("./pages/OverviewPage").then((module) => ({ default: module.OverviewPage })));
const SalesPage = lazy(() => import("./pages/SalesPage").then((module) => ({ default: module.SalesPage })));
const ProductsPage = lazy(() => import("./pages/ProductsPage").then((module) => ({ default: module.ProductsPage })));
const ProductDetailPage = lazy(() => import("./pages/ProductDetailPage").then((module) => ({ default: module.ProductDetailPage })));
const PurchasesPage = lazy(() => import("./pages/PurchasesPage").then((module) => ({ default: module.PurchasesPage })));
const OperationsPage = lazy(() => import("./pages/OperationsPage").then((module) => ({ default: module.OperationsPage })));
const LeaderboardsPage = lazy(() => import("./pages/LeaderboardsPage").then((module) => ({ default: module.LeaderboardsPage })));
const StaffPage = lazy(() => import("./pages/StaffPage").then((module) => ({ default: module.StaffPage })));
const StaffDetailPage = lazy(() => import("./pages/StaffPage").then((module) => ({ default: module.StaffDetailPage })));
const TransactionsPage = lazy(() => import("./pages/TransactionsPage").then((module) => ({ default: module.TransactionsPage })));
const AdminPage = lazy(() => import("./pages/AdminPage").then((module) => ({ default: module.AdminPage })));

export default function App() {
  const [user, setUser] = useState<SessionUser | null | undefined>(undefined);

  useEffect(() => {
    api<{ user: SessionUser }>("/api/auth/me")
      .then(({ user: current }) => { setCsrfToken(current.csrfToken); setUser(current); })
      .catch(() => setUser(null));
  }, []);

  if (user === undefined) return <div className="boot-screen"><Loading label="Opening your private dashboard" /></div>;
  if (!user) return <LoginPage />;

  return <Suspense fallback={<div className="route-loading"><Loading label="Loading workspace" /></div>}><Routes>
    <Route element={<Layout user={user} onLogout={() => { setCsrfToken(""); setUser(null); }} />}>
      <Route index element={<OverviewPage />} />
      <Route path="sales" element={<SalesPage />} />
      <Route path="products" element={<ProductsPage />} />
      <Route path="products/:productId" element={<ProductDetailPage />} />
      <Route path="purchases" element={<PurchasesPage />} />
      <Route path="operations" element={<OperationsPage />} />
      <Route path="leaderboards" element={<LeaderboardsPage />} />
      <Route path="staff" element={<StaffPage />} />
      <Route path="staff/:staffName" element={<StaffDetailPage />} />
      <Route path="transactions" element={<TransactionsPage />} />
      <Route path="admin" element={user.role === "owner" ? <AdminPage /> : <Navigate to="/" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes></Suspense>;
}
